import { Document, Image, Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import React, { useEffect, useState } from 'react';

function NewPackageDownloadPdf({
    packageData,
    imageData,
    logo,
    userDetails,
    userType,
    selectedCategory,
    allPackageHotels,
    calculatePriceWithMarkup,
    selectedOption,
    selectedMealPlan = "ep",
}) {

    // const [base64Assets, setBase64Assets] = useState({
    //     logo: null,
    //     hotelImage: [],
    //     // destinations: [],
    // });

    const stripHtmlTags = (html) => html?.replace(/<[^>]*>?/gm, '');

    const formatHtmlToText = (html) => {
        return html
            ?.replace(/<p>/gi, '\n\n')             // Convert opening <p> tags to double line breaks
            .replace(/<\/p>/gi, '')                // Remove closing </p> tags
            .replace(/<br\s*\/?>/gi, '\n')         // Replace <br> tags with single line breaks
            .replace(/&nbsp;/gi, ' ')              // Replace non-breaking spaces with regular spaces
            .replace(/<[^>]*>/g, '')               // Remove all other HTML tags
            .trim();                               // Clean up extra spaces at the beginning or end
    };

    function checkDestinationCount(destination, data) {
        if (!destination) return;

        // Create a new array excluding the last item
        const filteredData = data?.slice(0, -1);
        const destinationLowerCase = destination?.toLowerCase();

        // Check the count of matching destinations in the filtered array
        const count = filteredData?.filter(
            (item) => item.endDestination?.toLowerCase() === destinationLowerCase
        )?.length;
        return count;
    };

    const styles = StyleSheet.create({
        page: {
            paddingTop: 10,
            paddingBottom: 10,
            // fontFamily: 'Helvetica',
        },
        header: {
            paddingTop: 6,
            paddingRight: 15,
            paddingBottom: 6,
            paddingLeft: 15,
            gap: 10,
            display: "flex",
            alignItems: 'center',
        },
        logo: {
            width: 52,
            height: 50,
            objectFit: 'contain'
        },
        row: {
            flexDirection: "row"
        },
        space_between: {
            justifyContent: "space-between"
        },
        itemsCenter: {
            alignItems: 'center'
        },
        width25: {
            width: "25%"
        },
        gap_6: {
            gap: 6
        },
        gap_10: {
            gap: 10
        },
        gap_15: {
            gap: 15
        },
        flex_wrap: {
            flexWrap: "wrap"
        },
        relative: {
            position: 'relative'
        },
        absolute: {
            position: 'absolute'
        },
        titleContainer: {
            paddingTop: 12,
            paddingRight: 15,
            paddingBottom: 12,
            paddingLeft: 15,
            gap: 6,
            backgroundColor: "#009A50",
        },
        tripTitle: {
            fontSize: 16,
            fontWeight: 600,
            color: 'white',
        },
        subTitle: {
            fontSize: 10,
            fontWeight: 400,
            color: 'white',
        },
        sectionTitle: {
            fontSize: 14,
            fontWeight: 500,
            color: '#333333',
        },
        singlePersonRates: {
            fontSize: 10,
            fontWeight: 400,
            color: '#333333',
            marginTop: 10
        },
        wrapper: {
            gap: 15,
            paddingTop: 20,
            paddingRight: 15,
            paddingBottom: 20,
            paddingLeft: 15,
            borderBottomWidth: 0.5,
            borderBottomStyle: 'solid',
            borderBottomColor: '#333333',
        },
        policiesWrapper: {
            gap: 10,
            borderWidth: 0.5,
            borderStyle: 'solid',
            borderColor: '#000000',
            padding: 10,
        },
        card: {
            width: '24%',
        },
        image: {
            width: '100%',
        },
        caption: {
            fontSize: 12,
            fontWeight: 400,
            color: '#333333'
        },
        custom: {
            paddingTop: 4,
            paddingRight: 5,
            paddingBottom: 4,
            paddingLeft: 5,
            gap: 3,
            borderWidth: 0.5,
            borderStyle: 'solid',
            borderColor: '#ECB119',
            backgroundColor: '#FDF7E4'
        },
        tableCell: {
            gap: 10,
            paddingTop: 5,
            paddingRight: 10,
            paddingBottom: 5,
            paddingLeft: 10,

            borderRightWidth: 0.5,
            borderRightStyle: 'solid',
            borderRightColor: '#333333',
        },
        borderRight: {
            borderRightWidth: 0.5,
            borderRightStyle: 'solid',
            borderRightColor: '#333333',
        },
        border: {
            borderWidth: 0.25,
            borderStyle: 'solid',
            borderColor: '#333333'
        },
    });

    const fetchImageAsBase64 = async (imageUrl) => {
        if (!imageUrl || typeof imageUrl !== "string") {
            console.error("fetchImageAsBase64: Invalid image URL:", imageUrl);
            return null;
        }

        try {
            const response = await fetch(imageUrl, {
                mode: "cors",
                cache: "no-store",
            });

            if (!response.ok) {
                console.error(
                    "fetchImageAsBase64: Failed to fetch image",
                    response.statusText
                );
                return null;
            }

            const blob = await response.blob();

            return await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = () => reject("FileReader failed");
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("fetchImageAsBase64: Error occurred", error);
            return null;
        }
    };

    const groupHotelsByCategory = (hotels) => {
        return (
            hotels &&
            hotels.length > 0 &&
            hotels?.reduce((acc, hotel) => {
                if (!acc[hotel.hotelcategory]) {
                    acc[hotel.hotelcategory] = [];
                }
                acc[hotel.hotelcategory].push(hotel);
                return acc;
            }, {})
        );
    };

    const groupedHotels = groupHotelsByCategory(allPackageHotels);

    const DestinationImage = ({ url, style }) => {
        const [destBase64Url, setDestBase64Url] = useState(null);

        useEffect(() => {
            if (url) {
                fetchImageAsBase64(url).then(setDestBase64Url);
            }
        }, [url]);

        return <Image src={destBase64Url || url} style={style} />;
    };

    const HotelImage = ({ url, style }) => {
        const [base64Url, setBase64Url] = useState(null);

        useEffect(() => {
            if (url) {
                fetchImageAsBase64(url).then(setBase64Url);
            }
        }, [url]);

        return <Image src={base64Url || url} style={style} />;
    };

    // const [logoBase64Url, setLogoBase64Url] = useState(null);

    // useEffect(() => {
    //     const getBase64Logo = async () => {
    //         setIsLoading(true);
    //         const base64 = await fetchImageAsBase64(imageData || "");
    //         setLogoBase64Url(base64);
    //     };

    //     getBase64Logo();
    // }, [imageData]);

    return (
        <Document>
            <Page style={styles.page}>
                <View style={styles.header}>
                    {imageData ?
                        <Image style={styles.logo} src={imageData} />
                        :
                        <Image style={styles.logo} src={logo} />
                    }
                </View>

                {/* HEADER SECTION */}
                <View style={styles.titleContainer}>
                    <View style={styles.row}>
                        <Text style={styles.tripTitle}>
                            {packageData?.packageName}
                        </Text>
                    </View>

                    <View style={[styles.space_between, styles.row]}>
                        <View style={[styles.gap_10, styles.row]}>
                            <Text style={styles.subTitle}>
                                {packageData?.totalNights}N
                                {" "}
                                /
                                {" "}
                                {packageData?.totalDays}D
                            </Text>
                            <View style={[styles.row, styles.itemsCenter, { gap: 3 }]}>
                                <Svg width="12" height="12" viewBox="0 0 12 12">
                                    <Path
                                        d="M6.00095 5.9325C6.2497 5.9325 6.46237 5.84392 6.63895 5.66675C6.81562 5.48959 6.90395 5.27663 6.90395 5.02788C6.90395 4.77913 6.81537 4.56642 6.6382 4.38975C6.46103 4.21317 6.24803 4.12488 5.9992 4.12488C5.75045 4.12488 5.53778 4.21346 5.3612 4.39063C5.18453 4.56779 5.0962 4.78079 5.0962 5.02963C5.0962 5.27838 5.18478 5.49104 5.36195 5.66763C5.53912 5.84421 5.75212 5.9325 6.00095 5.9325ZM6.00007 9.75663C6.97824 8.88096 7.72678 8.04104 8.2457 7.23688C8.76462 6.43271 9.02407 5.72838 9.02407 5.12388C9.02407 4.21238 8.73449 3.46304 8.15532 2.87588C7.57616 2.28871 6.85774 1.99513 6.00007 1.99513C5.14241 1.99513 4.42399 2.28871 3.84482 2.87588C3.26566 3.46304 2.97607 4.21238 2.97607 5.12388C2.97607 5.72838 3.23553 6.43271 3.75445 7.23688C4.27337 8.04104 5.02191 8.88096 6.00007 9.75663ZM6.00007 10.7546C4.74174 9.6643 3.79816 8.64959 3.16932 7.7105C2.54049 6.77134 2.22607 5.90913 2.22607 5.12388C2.22607 3.97004 2.59928 3.03592 3.3457 2.3215C4.0922 1.60709 4.97699 1.24988 6.00007 1.24988C7.02316 1.24988 7.90795 1.60709 8.65445 2.3215C9.40087 3.03592 9.77407 3.97004 9.77407 5.12388C9.77407 5.90913 9.45966 6.77134 8.83082 7.7105C8.20199 8.64959 7.25841 9.6643 6.00007 10.7546Z"
                                        fill="white"
                                    />
                                </Svg>
                                <Text style={styles.subTitle}>{packageData?.packageHub}</Text>
                            </View>
                        </View>

                        <View style={[styles.gap_10, styles.row]}>
                            {packageData?.packageDestination && packageData?.packageDestination?.length > 0 && (
                                <Text style={styles.subTitle}>{packageData?.packageDestination.length} Destinations</Text>
                            )}
                            {groupedHotels && (
                                <Text style={styles.subTitle}> {groupedHotels[selectedCategory]?.length || 0} Stays</Text>
                            )}
                            {/* <Text style={styles.subTitle}>1 Transport</Text> */}
                        </View>
                    </View>
                </View>

                {/* DESTINATIONS */}
                <View style={styles.wrapper}>
                    <View>
                        <Text style={styles.sectionTitle}>Destinations</Text>
                    </View>

                    <View style={styles.gap_10}>
                        <View style={[styles.gap_6, styles.row, styles.flex_wrap]}>
                            {packageData &&
                                packageData?.packageDestination &&
                                packageData?.packageDestination?.length > 0 &&
                                packageData?.packageDestination?.map((packagedestinations, index) => (
                                    <View
                                        key={index}
                                        style={[styles.card, styles.gap_6]}
                                    >
                                        <DestinationImage
                                            url={packagedestinations?.base64Image || packagedestinations?.image}
                                            style={[styles.image, { height: 100 }]}
                                        />
                                        <Text style={styles.caption}> {packagedestinations?.name}
                                            <Text> ({checkDestinationCount(
                                                packagedestinations?.name,
                                                packageData?.packageItinerary
                                            ) + "N"}
                                                )</Text>
                                        </Text>
                                    </View>
                                ))}
                            {/* </>
                                )
                            } */}
                        </View>
                    </View>
                </View>

                {/* DAY WISE */}
                <View style={styles.wrapper}>
                    <View>
                        <Text style={styles.sectionTitle}>Day Wise Itinerary</Text>
                    </View>

                    <View style={styles.gap_15}>
                        {packageData &&
                            packageData?.packageItinerary &&
                            packageData?.packageItinerary?.length > 0 &&
                            packageData?.packageItinerary
                                ?.map((packItinerary, index) => (
                                    <View style={{ border: '1px solid #333333' }} key={index} wrap={false}>
                                        <View style={[styles.row, { padding: 10, justifyContent: 'space-between', borderBottom: '1px solid #333333' }]}>
                                            <View style={[styles.gap_10, styles.row, { alignItems: 'center' }]}>
                                                <Text style={[styles.sectionTitle, { color: '#009A50' }]}>Day{" "}{packItinerary?.day}</Text>
                                                <View style={[styles.gap_10, styles.row]}>
                                                    <Text style={[styles.subTitle, { color: "#333333", textTransform: 'capitalize' }]}>Starts: {packItinerary.startDestination}</Text>
                                                    <Text style={[styles.subTitle, { color: "#333333", textTransform: 'capitalize' }]}>Ends: {packItinerary.endDestination}</Text>
                                                </View>
                                            </View>

                                            {/* <View
                                                style={[styles.gap_6, styles.row, { alignItems: 'center' }]}
                                                key={`${category}-${index}`}
                                            >
                                                <View>
                                                    <Svg width="15" height="15" viewBox="0 0 15 15">
                                                        <Path
                                                            d="M2.54624 12.3933C2.30027 12.3933..."
                                                            fill="#333333"
                                                        />
                                                    </Svg>
                                                </View>
                                                <View>
                                                    <Text style={[styles.subTitle, { color: "#333333" }]}>
                                                        {itemData?.name}
                                                    </Text>
                                                </View>
                                            </View> */}

                                            {Object.entries(groupedHotels)?.map(
                                                ([category, hotelsselected]) => {
                                                    if (selectedCategory && category !== selectedCategory) {
                                                        return null;
                                                    }

                                                    const groupByCategory = packageData?.packageRates.filter(
                                                        (item) => item.accommodationType === category
                                                    );

                                                    return (
                                                        <>
                                                            {hotelsselected?.filter(
                                                                (item) => item?.city.toLowerCase() === packItinerary?.endDestination.toLowerCase()
                                                            )?.slice(0, 1)?.map((hotel, index) => (
                                                                <View
                                                                    style={[styles.gap_6, styles.row, { alignItems: 'center' }]}
                                                                    key={index}
                                                                >
                                                                    <View>
                                                                        <Svg width="15" height="15" viewBox="0 0 15 15">
                                                                            <Path
                                                                                d="M2.58957 13.718C2.30846 13.718 2.0694 13.6195 1.8724 13.4225C1.6754 13.2254 1.5769 12.9863 1.5769 12.7052V3.064C1.5769 2.78289 1.6754 2.54383 1.8724 2.34683C2.0694 2.14983 2.30846 2.05133 2.58957 2.05133H6.8974C7.17852 2.05133 7.41757 2.14983 7.61457 2.34683C7.81157 2.54383 7.91007 2.78289 7.91007 3.064V4.718H13.4102C13.6913 4.718 13.9304 4.8165 14.1274 5.0135C14.3244 5.2105 14.4229 5.44955 14.4229 5.73066V12.7052C14.4229 12.9863 14.3244 13.2254 14.1274 13.4225C13.9304 13.6195 13.6913 13.718 13.4102 13.718H2.58957ZM2.57674 12.718H6.91024V11.0513H2.57674V12.718ZM2.57674 10.0513H6.91024V8.38466H2.57674V10.0513ZM2.57674 7.38466H6.91024V5.718H2.57674V7.38466ZM2.57674 4.718H6.91024V3.05133H2.57674V4.718ZM7.91007 12.718H13.4231V5.718H7.91007V12.718ZM9.87174 8.38466C9.73007 8.38466 9.61129 8.33672 9.5154 8.24083C9.41963 8.14494 9.37174 8.02611 9.37174 7.88433C9.37174 7.74266 9.41963 7.62394 9.5154 7.52816C9.61129 7.4325 9.73007 7.38466 9.87174 7.38466H11.3332C11.4749 7.38466 11.5936 7.43261 11.6894 7.5285C11.7853 7.62428 11.8332 7.74305 11.8332 7.88483C11.8332 8.02661 11.7853 8.14533 11.6894 8.241C11.5936 8.33677 11.4749 8.38466 11.3332 8.38466H9.87174ZM9.87174 11.0513C9.73007 11.0513 9.61129 11.0034 9.5154 10.9075C9.41963 10.8116 9.37174 10.6928 9.37174 10.551C9.37174 10.4093 9.41963 10.2906 9.5154 10.1948C9.61129 10.0992 9.73007 10.0513 9.87174 10.0513H11.3332C11.4749 10.0513 11.5936 10.0993 11.6894 10.1952C11.7853 10.2909 11.8332 10.4097 11.8332 10.5515C11.8332 10.6933 11.7853 10.812 11.6894 10.9077C11.5936 11.0034 11.4749 11.0513 11.3332 11.0513H9.87174Z"
                                                                                fill="#333333"
                                                                            />
                                                                        </Svg>
                                                                    </View>
                                                                    <View>
                                                                        <Text style={[styles.subTitle, { color: "#333333" }]}>
                                                                            {hotel?.name}
                                                                        </Text>
                                                                    </View>
                                                                </View>
                                                            ))}
                                                        </>
                                                    )
                                                }
                                            )}

                                        </View>
                                        <View style={[styles.gap_10, { padding: 10 }]}>
                                            <Text style={[styles.subTitle, { color: "#333333" }]}>
                                                {stripHtmlTags(packItinerary?.additionalDetails?.short_description)}
                                            </Text>
                                        </View>
                                    </View>
                                ))}
                    </View>
                </View>

                {/* STAYS */}
                <View style={styles.wrapper} wrap={false}>
                    <View>
                        <Text style={styles.sectionTitle}>Stays</Text>
                    </View>

                    {selectedCategory && (
                        <View style={styles.row}>
                            <View style={[styles.custom, styles.row, { alignItems: 'center' }]}>
                                <Svg width="15" height="15" viewBox="0 0 15 15">
                                    <Path
                                        d="M4.125 9.70726L6 8.27851L7.875 9.70726L7.17312 7.38614L9.04325 6.05739H6.75188L6 3.62076L5.24813 6.05739H2.95675L4.82687 7.38614L4.125 9.70726ZM6.00087 11.64C5.34387 11.64 4.72633 11.5153 4.14825 11.266C3.57017 11.0167 3.06733 10.6783 2.63975 10.2509C2.21217 9.82347 1.87362 9.32085 1.62412 8.74301C1.37471 8.16518 1.25 7.54781 1.25 6.89089C1.25 6.23389 1.37467 5.61635 1.624 5.03826C1.87333 4.46018 2.21171 3.95735 2.63913 3.52976C3.06654 3.10218 3.56917 2.76364 4.147 2.51414C4.72483 2.26472 5.34221 2.14001 5.99913 2.14001C6.65613 2.14001 7.27367 2.26468 7.85175 2.51401C8.42983 2.76335 8.93267 3.10172 9.36025 3.52914C9.78783 3.95656 10.1264 4.45918 10.3759 5.03701C10.6253 5.61485 10.75 6.23222 10.75 6.88914C10.75 7.54614 10.6253 8.16368 10.376 8.74177C10.1267 9.31985 9.78829 9.82268 9.36088 10.2503C8.93346 10.6778 8.43083 11.0164 7.853 11.2659C7.27517 11.5153 6.65779 11.64 6.00087 11.64Z"
                                        fill="#ECB119"
                                    />
                                </Svg>
                                <Text style={[styles.subTitle, { color: "#333333", textTransform: 'uppercase' }]}>{selectedCategory}</Text>
                            </View>
                        </View>
                    )}

                    {Object.entries(groupedHotels).map(
                        ([category, hotelsselected]) => {
                            if (selectedCategory && category !== selectedCategory) {
                                return null;
                            }

                            const groupByCategory = packageData?.packageRates.filter(
                                (item) => item.accommodationType === category
                            );

                            return (
                                <View style={[styles.gap_15]} key={category}>
                                    <View style={[styles.flex_wrap, styles.row, styles.relative, styles.gap_6]}>
                                        {hotelsselected?.map((hotel, index) => (
                                            <View style={[styles.card, styles.gap_6]} key={index}>
                                                <Image
                                                    src={hotel?.base64FeaturedImage || hotel?.featuredImage}
                                                    style={[styles.image, { height: 100 }]}
                                                />
                                                {/* <HotelImage
                                                    url={hotel.featuredImage}
                                                    style={[styles.image, { height: 100 }]}
                                                /> */}

                                                <Text style={[styles.subTitle, styles.absolute, { backgroundColor: "#009A50", padding: 3 }]}>
                                                    {hotel?.city}
                                                </Text>

                                                <View style={{ gap: 3 }}>
                                                    <Text style={styles.caption}>{hotel?.name}</Text>
                                                    <Text style={[styles.subTitle, { color: "#888888" }]}>{hotel.starRating} Stars</Text>
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )
                        }
                    )}
                </View>

                {/* POLICIES */}
                <View style={styles.wrapper} wrap={true}>
                    <View wrap={false}>
                        <View style={styles.gap_6}>
                            <View>
                                <Text style={styles.sectionTitle}>Policies</Text>
                            </View>

                            <View style={styles.policiesWrapper} >
                                <View>
                                    <Text style={styles.caption}>Inclusions & Exclusions</Text>
                                </View>

                                {/* INCLUSIONS */}


                                <View style={styles.gap_10}>
                                    {packageData && packageData?.packagePolicies && packageData?.packagePolicies?.length > 0 && packageData?.packagePolicies
                                        ?.filter(policy => policy.type === 'inclusion')
                                        ?.map((policy, idx) => (
                                            <View wrap={false} key={idx}>
                                                <Text style={[styles.subTitle, { color: '#333333' }]}>
                                                    Inclusions
                                                </Text>
                                                <View style={[styles.gap_6, styles.row, styles.itemsCenter, { width: '100%' }]} key={idx}>
                                                    {/* <View style={{ width: "5%" }}>
                                                        <Svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <mask id="mask0_8081_6479" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                                                                <rect width="16" height="16" fill="#D9D9D9" />
                                                            </mask>
                                                            <g mask="url(#mask0_8081_6479)">
                                                                <Path d="M6.36651 10.3437L12.1255 4.58468C12.2246 4.48546 12.3406 4.43479 12.4735 4.43268C12.6064 4.43057 12.7246 4.48124 12.828 4.58468C12.9315 4.68812 12.9832 4.8069 12.9832 4.94101C12.9832 5.07524 12.9315 5.19407 12.828 5.29751L6.78834 11.3475C6.66779 11.468 6.52717 11.5282 6.36651 11.5282C6.20584 11.5282 6.06523 11.468 5.94467 11.3475L3.16134 8.56418C3.06223 8.46496 3.01334 8.34718 3.01467 8.21085C3.0159 8.07462 3.06823 7.95479 3.17167 7.85135C3.27512 7.7479 3.3939 7.69618 3.52801 7.69618C3.66223 7.69618 3.78106 7.7479 3.88451 7.85135L6.36651 10.3437Z" fill="#009A50" />
                                                            </g>
                                                        </Svg>
                                                    </View> */}
                                                    <View style={{ width: '95%' }}>
                                                        <Text style={[styles.subTitle, { color: "#666666" }]}>
                                                            {formatHtmlToText(policy?.content)}
                                                        </Text>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}

                                    {/* EXCLUSIONS */}
                                    {packageData && packageData?.packagePolicies && packageData.packagePolicies.length > 0 && packageData.packagePolicies
                                        ?.filter(policy => policy.type === 'exclusion')
                                        ?.map((policy, idx) => (
                                            <View wrap={false} key={idx}>
                                                <Text style={[styles.subTitle, { color: '#333333' }]}>
                                                    Exclusions
                                                </Text>
                                                <View style={[styles.gap_6, styles.row, styles.itemsCenter, { width: '100%' }]} key={idx}>
                                                    {/* <View style={{ width: '5%' }}>
                                                        <Svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <mask id="mask0_8081_6502" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                                                                <rect width="16" height="16" fill="#D9D9D9" />
                                                            </mask>
                                                            <g mask="url(#mask0_8081_6502)">
                                                                <Path d="M8.00001 8.70251L4.61801 12.0847C4.52567 12.1769 4.40962 12.2241 4.26984 12.2263C4.13017 12.2285 4.01201 12.1812 3.91534 12.0847C3.81879 11.988 3.77051 11.8709 3.77051 11.7333C3.77051 11.5958 3.81879 11.4787 3.91534 11.382L7.29751 8.00001L3.91534 4.61801C3.82312 4.52568 3.7759 4.40962 3.77367 4.26984C3.77156 4.13017 3.81879 4.01201 3.91534 3.91534C4.01201 3.81879 4.12912 3.77051 4.26667 3.77051C4.40423 3.77051 4.52134 3.81879 4.61801 3.91534L8.00001 7.29751L11.382 3.91534C11.4743 3.82312 11.5904 3.7759 11.7302 3.77367C11.8698 3.77156 11.988 3.81879 12.0847 3.91534C12.1812 4.01201 12.2295 4.12912 12.2295 4.26667C12.2295 4.40423 12.1812 4.52134 12.0847 4.61801L8.70251 8.00001L12.0847 11.382C12.1769 11.4743 12.2241 11.5904 12.2263 11.7302C12.2285 11.8698 12.1812 11.988 12.0847 12.0847C11.988 12.1812 11.8709 12.2295 11.7333 12.2295C11.5958 12.2295 11.4787 12.1812 11.382 12.0847L8.00001 8.70251Z" fill="#EB442D" />
                                                            </g>
                                                        </Svg>
                                                    </View> */}
                                                    <View style={{ width: '95%' }}>
                                                        <Text style={[styles.subTitle, { color: "#666666" }]}>
                                                            {formatHtmlToText(policy?.content)}
                                                        </Text>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}
                                </View>
                            </View>
                        </View>
                    </View>

                    {/* TERMS AND CONDITIONS */}

                    {packageData &&
                        packageData?.packagePolicies &&
                        packageData.packagePolicies.length > 0 &&
                        packageData.packagePolicies
                            ?.filter(policy => policy.type === 'terms_and_conditions') && (
                            <View wrap={false}>
                                <View style={styles.gap_6}>
                                    <View>
                                        <Text style={styles.caption}>Terms & Conditions</Text>
                                    </View>

                                    <View style={styles.policiesWrapper}>
                                        <View style={styles.gap_6}>
                                            {packageData && packageData?.packagePolicies && packageData.packagePolicies.length > 0 && packageData.packagePolicies
                                                ?.filter(policy => policy.type === 'terms_and_conditions')
                                                ?.map((policy, idx) => (
                                                    <View style={[styles.gap_6, styles.row, styles.itemsCenter, { width: '100%' }]} key={idx}>
                                                        {/* <View style={{ width: "5%" }}>
                                                    <Text style={[styles.subTitle, { color: '#666666' }]}>•</Text>
                                                </View> */}
                                                        <View style={{ width: "95%" }}>
                                                            <Text style={[styles.subTitle, { color: '#666666' }]}>{formatHtmlToText(policy?.content)}</Text>
                                                        </View>
                                                    </View>
                                                ))}
                                        </View>
                                    </View>
                                </View>
                            </View>
                        )}

                    {/* CANCELLATION POLICIES */}
                    {packageData &&
                        packageData?.packagePolicies &&
                        packageData.packagePolicies.length > 0 &&
                        packageData.packagePolicies
                            ?.filter(policy => policy.type === 'cancellation_policy') && (
                            <View wrap={false}>
                                <View style={styles.gap_6}>
                                    <View>
                                        <Text style={styles.caption}>Cancellation Policy</Text>
                                    </View>
                                    <View style={styles.policiesWrapper}>
                                        <View style={styles.gap_6}>
                                            {packageData && packageData?.packagePolicies && packageData.packagePolicies.length > 0 && packageData.packagePolicies
                                                ?.filter(policy => policy.type === 'cancellation_policy')
                                                ?.map((policy, idx) => (
                                                    <View style={[styles.gap_6, styles.row, styles.itemsCenter, { width: "100%" }]} key={idx}>
                                                        {/* <View style={{ width: "5%" }}>
                                                            <Text style={[styles.subTitle, { color: '#666666' }]}>•</Text>
                                                        </View> */}
                                                        <View style={{ width: "95%" }}>
                                                            <Text style={[styles.subTitle, { color: '#666666' }]}>{formatHtmlToText(policy?.content)}</Text>
                                                        </View>
                                                    </View>
                                                ))}
                                        </View>
                                    </View>
                                </View>
                            </View>
                        )}
                </View>

                {/* PACKAGES RATES */}
                {Object.entries(groupedHotels).map(
                    ([category, hotelsselected]) => {
                        if (selectedCategory && category !== selectedCategory) {
                            return null;
                        }

                        const groupByCategory = packageData?.packageRates.filter(
                            (item) => item.accommodationType === category
                        );

                        return (
                            <View style={styles.wrapper} wrap={true}>
                                <View>
                                    <Text style={styles.sectionTitle}>Packages Rates</Text>
                                    <Text style={styles.singlePersonRates}>Note: These are single-person rates.</Text>
                                </View>

                                <View>
                                    <View style={[styles.row, styles.gap_10]}>
                                        {selectedCategory && (
                                            <Text style={styles.caption}>Stay: <Text style={[styles.caption, { color: '#EB442D', textTransform: 'uppercase' }]}>{selectedCategory}</Text></Text>
                                        )}
                                        {selectedMealPlan && (
                                            <Text style={styles.caption}>Meal Plan: <Text style={[styles.caption, { color: '#EB442D', textTransform: 'uppercase' }]}>{selectedMealPlan}</Text></Text>
                                        )}
                                    </View>
                                </View>

                                {/* EP Rates */}
                                {selectedMealPlan === "ep" && groupByCategory.filter(
                                    (item) => item.mealPlan === "ep"
                                ).length > 0 && (
                                        <View style={{ border: '0.25px solid #333333' }}>
                                            {/* <Text style={{ ...styles.contentRow }}>EP</Text> */}
                                            <View style={[styles.row, { backgroundColor: "#009A50", fontWeight: 500 }]} wrap={false}>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>PAX</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Price</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Extra Adult</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Child WB/WOB</Text>
                                                </View>
                                            </View>
                                            {groupByCategory
                                                .filter(
                                                    (item) =>
                                                        item.mealPlan === "ep" &&
                                                        item.accommodationType === category
                                                )
                                                .map((hotel, index) => (
                                                    <View
                                                        key={index}
                                                        style={[styles.row, styles.border, { fontWeight: 500 }]}
                                                        wrap={false}>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>{hotel?.pax} PAX</Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.price
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.extraAdult || "N/A"
                                                                )}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWBed || "N/A"
                                                                )}{" "}
                                                                /{" "}
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWoBed
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                    </View>
                                                ))}
                                        </View>
                                    )}

                                {/* CP Rates */}
                                {selectedMealPlan === "cp" && groupByCategory.filter(
                                    (item) => item.mealPlan === "cp"
                                ).length > 0 && (
                                        <View style={{ border: '0.25px solid #333333' }}>
                                            {/* <Text style={{ ...styles.contentRow }}>CP</Text> */}
                                            <View style={[styles.row, { backgroundColor: "#009A50", fontWeight: 500 }]} wrap={false}>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>PAX</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Price</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Extra Adult</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Child WB/WOB</Text>
                                                </View>
                                            </View>
                                            {groupByCategory
                                                .filter(
                                                    (item) =>
                                                        item.mealPlan === "cp" &&
                                                        item.accommodationType === category
                                                )
                                                .map((hotel, index) => (
                                                    <View
                                                        key={index}
                                                        style={[styles.row, styles.border, { fontWeight: 500 }]}
                                                        wrap={false}>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>{hotel?.pax} PAX</Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.price
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.extraAdult || "N/A"
                                                                )}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWBed || "N/A"
                                                                )}{" "}
                                                                /{" "}
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWoBed
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                    </View>
                                                ))}
                                        </View>
                                    )}

                                {/* MAP Rates */}
                                {selectedMealPlan === "map" && groupByCategory.filter(
                                    (item) => item.mealPlan === "map"
                                ).length > 0 && (
                                        <View style={{ border: '0.25px solid #333333' }}>
                                            {/* <Text style={{ ...styles.contentRow }}>Map</Text> */}
                                            <View style={[styles.row, { backgroundColor: "#009A50", fontWeight: 500 }]} wrap={false}>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>PAX</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Price</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Extra Adult</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Child WB/WOB</Text>
                                                </View>
                                            </View>
                                            {groupByCategory
                                                .filter(
                                                    (item) =>
                                                        item.mealPlan === "map" &&
                                                        item.accommodationType === category
                                                )
                                                .map((hotel, index) => (
                                                    <View
                                                        key={index}
                                                        style={[styles.row, styles.border, { fontWeight: 500 }]}
                                                        wrap={false}>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>{hotel?.pax} PAX</Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.price
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.extraAdult || "N/A"
                                                                )}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWBed || "N/A"
                                                                )}{" "}
                                                                /{" "}
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWoBed
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                    </View>
                                                ))}
                                        </View>
                                    )}

                                {/* AP Rates */}
                                {selectedMealPlan === "ap" && groupByCategory.filter(
                                    (item) => item.mealPlan === "ap"
                                ).length > 0 && (
                                        <View style={{ border: '0.25px solid #333333' }}>
                                            {/* <Text style={{ ...styles.contentRow }}>AP</Text> */}
                                            <View style={[styles.row, { backgroundColor: "#009A50", fontWeight: 500 }]} wrap={false}>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>PAX</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Price</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Extra Adult</Text>
                                                </View>
                                                <View style={[styles.width25, styles.tableCell]}>
                                                    <Text style={[styles.subTitle]}>Child WB/WOB</Text>
                                                </View>
                                            </View>
                                            {groupByCategory
                                                .filter(
                                                    (item) =>
                                                        item.mealPlan === "ap" &&
                                                        item.accommodationType === category
                                                )
                                                .map((hotel, index) => (
                                                    <View
                                                        key={index}
                                                        style={[styles.row, styles.border, { fontWeight: 500 }]}
                                                        wrap={false}>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>{hotel?.pax} PAX</Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.price
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.extraAdult || "N/A"
                                                                )}
                                                            </Text>
                                                        </View>
                                                        <View style={[styles.width25, styles.tableCell]}>
                                                            <Text style={[styles.subTitle, { color: "black" }]}>
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWBed || "N/A"
                                                                )}{" "}
                                                                /{" "}
                                                                {calculatePriceWithMarkup(
                                                                    hotel?.childWoBed
                                                                ) || "N/A"}
                                                            </Text>
                                                        </View>
                                                    </View>
                                                ))}
                                        </View>
                                    )}
                            </View>
                        );
                    }
                )}

                {/* CONTACT */}
                <View style={styles.wrapper} wrap={false}>
                    <View style={styles.gap_10}>
                        <View>
                            <Text style={[styles.subTitle, { color: "#009A50" }]}>CONTACT FOR MORE DETAILS:</Text>
                        </View>

                        <View style={[styles.gap_15, styles.row]}>
                            <View style={[styles.gap_6, styles.row, styles.itemsCenter]}>
                                <Svg width="15" height="15" viewBox="0 0 15 15">
                                    <Path
                                        d="M12.9603 13.6666C11.704 13.6666 10.4418 13.3745 9.17383 12.7903C7.90594 12.206 6.741 11.3819 5.679 10.3179C4.61711 9.25381 3.79405 8.08881 3.20983 6.82292C2.62561 5.55714 2.3335 4.29609 2.3335 3.03975C2.3335 2.83786 2.40016 2.66964 2.5335 2.53509C2.66683 2.40053 2.8335 2.33325 3.0335 2.33325H5.20783C5.37616 2.33325 5.52466 2.3882 5.65333 2.49809C5.782 2.60786 5.86383 2.74353 5.89883 2.90509L6.281 4.86659C6.30744 5.04859 6.30188 5.20497 6.26433 5.33575C6.22666 5.46653 6.15911 5.57636 6.06166 5.66525L4.522 7.16408C4.76977 7.61786 5.05288 8.04714 5.37133 8.45192C5.68966 8.85659 6.03433 9.24309 6.40533 9.61142C6.77111 9.97731 7.16 10.3171 7.572 10.6308C7.984 10.9444 8.42888 11.2363 8.90666 11.5064L10.4027 9.99742C10.507 9.88886 10.6333 9.81275 10.7817 9.76908C10.9299 9.72553 11.0839 9.71486 11.2438 9.73708L13.095 10.1141C13.2633 10.1585 13.4007 10.2444 13.5072 10.3718C13.6136 10.4991 13.6668 10.6435 13.6668 10.8051V12.9666C13.6668 13.1666 13.5996 13.3333 13.465 13.4666C13.3304 13.5999 13.1622 13.6666 12.9603 13.6666ZM4.04883 6.21792L5.23866 5.07942C5.26 5.06231 5.27388 5.03881 5.28033 5.00892C5.28677 4.97903 5.28572 4.95125 5.27716 4.92558L4.98733 3.43575C4.97877 3.40164 4.96383 3.37603 4.9425 3.35892C4.92116 3.34181 4.89338 3.33325 4.85916 3.33325H3.4335C3.40783 3.33325 3.38644 3.34181 3.36933 3.35892C3.35233 3.37603 3.34383 3.39742 3.34383 3.42309C3.37794 3.87864 3.4525 4.34142 3.5675 4.81142C3.68238 5.28153 3.84283 5.75036 4.04883 6.21792ZM9.84883 11.9794C10.2907 12.1854 10.7516 12.3429 11.2315 12.4519C11.7115 12.5608 12.16 12.6255 12.577 12.6461C12.6027 12.6461 12.6241 12.6375 12.6412 12.6204C12.6583 12.6033 12.6668 12.5819 12.6668 12.5563V11.1538C12.6668 11.1195 12.6583 11.0918 12.6412 11.0704C12.6241 11.0491 12.5984 11.0341 12.5643 11.0256L11.1643 10.7409C11.1387 10.7324 11.1162 10.7313 11.097 10.7378C11.0778 10.7442 11.0574 10.7581 11.036 10.7794L9.84883 11.9794Z"
                                        fill="#333333"
                                    />
                                </Svg>

                                <Text style={styles.caption}>
                                    Phone No. :{" "}
                                    {userType === "agent" && userDetails?.phone
                                        ? userDetails.phone
                                        : "+91 98160-12966"}
                                </Text>
                            </View>

                            <View style={[styles.gap_6, styles.row, styles.itemsCenter]}>
                                <Svg width="15" height="15" viewBox="0 0 15 15">
                                    <Path
                                        d="M2.87216 13C2.53538 13 2.25033 12.8833 2.01699 12.65C1.78366 12.4167 1.66699 12.1316 1.66699 11.7948V4.20517C1.66699 3.86839 1.78366 3.58333 2.01699 3.35C2.25033 3.11667 2.53538 3 2.87216 3H13.1285C13.4653 3 13.7503 3.11667 13.9837 3.35C14.217 3.58333 14.3337 3.86839 14.3337 4.20517V11.7948C14.3337 12.1316 14.217 12.4167 13.9837 12.65C13.7503 12.8833 13.4653 13 13.1285 13H2.87216ZM8.00033 8.37183L2.66699 4.9615V11.7948C2.66699 11.8547 2.68621 11.9039 2.72466 11.9423C2.7631 11.9808 2.81227 12 2.87216 12H13.1285C13.1884 12 13.2375 11.9808 13.276 11.9423C13.3144 11.9039 13.3337 11.8547 13.3337 11.7948V4.9615L8.00033 8.37183ZM8.00033 7.33333L13.2312 4H2.76949L8.00033 7.33333ZM2.66699 4.9615V4V11.7948C2.66699 11.8547 2.68621 11.9039 2.72466 11.9423C2.7631 11.9808 2.81227 12 2.87216 12H2.66699V4.9615Z"
                                        fill="#333333"
                                    />
                                </Svg>
                                <Text style={styles.caption}>
                                    Email:{" "}
                                    {userType === "agent" && userDetails?.email
                                        ? userDetails.email
                                        : "info@mountainmysteries.com"}
                                </Text>

                                {/* <Text style={styles.caption}>info@mountainmysteries.com</Text> */}
                            </View>
                        </View>
                        <View style={{ padding: 10, backgroundColor: '#FDF7E4F7' }}>
                            <Text style={[styles.subTitle, { color: "#333333" }]}>
                                This is one of our most popular holiday package to your chosen destination. The pricing is indicative and can change depending on hotels etc. chosen. Once your Travel Expert gets in touch with you, feel free to ask for any customisation in this itinerary or have a fresh itinerary designed, as per your preference. When you are making the booking, please do check that all the selected package inclusions are showing on the package review page and confirm that all elements including transportation, hotels and sightseeing are exactly as finalised by you.
                            </Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
}

export default NewPackageDownloadPdf;
